/**
 * Backfill the grid from a photograph of the paper card.
 *
 * Groups keep a paper card during the round — the app catches up at the turn,
 * or in the clubhouse, one photo at a time. A vision model reads the photo on
 * the server and answers with a PROPOSAL; this screen is where that proposal
 * meets a human who was actually on the holes.
 *
 * The one rule, from the design owner: backfill, never enter new information.
 * A proposed score lands only in a cell that is currently EMPTY, and only
 * after the scorer has seen it and left it ticked. A cell that already holds a
 * score is never writable from here — where the photo agrees it is shown
 * quietly as confirmation, and where it disagrees it is shown loudly as a
 * conflict the existing number wins. Fixing a genuinely wrong cell is the
 * ordinary score-edit path, on purpose: that path is visible, audited and
 * signed for, and a photo reader must not become a quieter way around it.
 *
 * Every confirmed cell goes through `setScore`, one call per cell — the same
 * door every tapped score walks through, with the outbox, the idempotency keys
 * and the birdie machinery behind it. A late birdie alert from a backfilled 3
 * is expected and fine; it happened, it just happened on paper first.
 */

import { useMemo, useState } from 'react'
import { clockTime } from '../engine/clock'
import { holesFor } from '../engine/handicap'
import type { HoleScore, HoleScoreValue, Tournament } from '../engine/types'
import { courseOf, playerName } from '../select'
import {
  getState,
  mergedScores,
  readCardPhoto,
  setScore,
  useStore,
  type CardPhotoCell,
} from '../store'

/**
 * One cell of the proposal, judged against what the card already holds.
 *
 *   propose  — the cell is empty and the photo offers a number. Writable,
 *              pre-ticked, editable, and the only kind that ever writes.
 *   agree    — the cell is filled and the photo read the same number. Nothing
 *              to do; shown quietly so the scorer can see the reader is sane.
 *   conflict — the cell is filled and the photo read something else. The
 *              existing score wins, always: it was entered by somebody with a
 *              name, possibly corrected since, possibly signed for. Shown
 *              loudly and NOT writable from here.
 */
export type ProposalCell =
  | { playerId: string; holeNo: number; kind: 'propose'; gross: number }
  | { playerId: string; holeNo: number; kind: 'agree'; gross: number }
  | { playerId: string; holeNo: number; kind: 'conflict'; proposed: number; existing: string }

/** What a filled cell says on the review grid — a number, or the state's initial. */
const existingLabel = (value: HoleScoreValue): string =>
  value.state === 'holed'
    ? String(value.gross)
    : value.state === 'pickedUp'
      ? (value.mostLikely != null ? `P${value.mostLikely}` : 'P')
      : 'C'

/**
 * Fold the server's proposal over what this phone believes the card holds.
 *
 * Judged against `mergedScores` — snapshot plus outbox — rather than the
 * snapshot alone, because a score tapped thirty seconds ago with no signal is
 * still a score, and proposing over it would be exactly the overwrite this
 * whole screen exists to prevent.
 *
 * "Agrees" means the cell is HOLED at the same gross. A picked-up or conceded
 * hole is a decision, not a number, and a photo that reads a digit off one of
 * those is disagreeing with the decision — which is a conflict, whatever the
 * digit. Pure and exported so the three-way judgement can be tested without a
 * photo, a model or a DOM.
 */
export function mergeProposal(
  roundId: string,
  cells: CardPhotoCell[],
  scores: HoleScore[],
): ProposalCell[] {
  const out: ProposalCell[] = []
  for (const cell of cells) {
    const existing = scores.find(
      (s) => s.roundId === roundId && s.playerId === cell.playerId && s.holeNo === cell.holeNo,
    )
    if (!existing) {
      out.push({ playerId: cell.playerId, holeNo: cell.holeNo, kind: 'propose', gross: cell.gross })
    } else if (existing.value.state === 'holed' && existing.value.gross === cell.gross) {
      out.push({ playerId: cell.playerId, holeNo: cell.holeNo, kind: 'agree', gross: cell.gross })
    } else {
      out.push({
        playerId: cell.playerId,
        holeNo: cell.holeNo,
        kind: 'conflict',
        proposed: cell.gross,
        existing: existingLabel(existing.value),
      })
    }
  }
  return out
}

/*
 * Shrink the photo before it travels.
 *
 * Phones hand over 12MP HEIC originals — five to eight megabytes of pixels the
 * model reads no better than a sharp 1800px JPEG, sent over the worst wifi in
 * Oregon. Decoding through an <img> rather than createImageBitmap because
 * Safari will decode HEIC for an image element and is the only browser these
 * twelve phones actually run; redrawing onto a canvas also strips the EXIF
 * block, which carried the GPS coordinates of wherever the card was signed.
 */
const LONGEST_EDGE_PX = 1800

async function toJpegBase64(file: File): Promise<string> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('That file could not be read as a photo.'))
      img.src = url
    })
    const scale = Math.min(1, LONGEST_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('This phone could not resize the photo.')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    return dataUrl.slice(dataUrl.indexOf(',') + 1)
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function PhotoBackfill({
  doc,
  roundId,
  onClose,
}: {
  doc: Tournament
  roundId: string
  onClose: () => void
}) {
  const scores = useStore(mergedScores)
  const myId = useStore((s) => s.session?.playerId)

  const round = doc.rounds.find((r) => r.id === roundId)
  const course = round ? courseOf(doc, round) : undefined
  const holes = useMemo(
    () => (course && round ? holesFor(course, round.nine) : []),
    [course, round],
  )
  const groups = doc.groups
    .filter((g) => g.roundId === roundId)
    .sort((a, b) => a.index - b.index)

  // The scorer is almost always photographing their own group's card, so start
  // there — but it is a choice, not an assumption: the man entering four cards
  // in the clubhouse is holding cards from every group.
  const [groupId, setGroupId] = useState(
    () => groups.find((g) => myId && g.playerIds.includes(myId))?.id ?? groups[0]?.id ?? '',
  )
  const [phase, setPhase] = useState<'pick' | 'reading' | 'review'>('pick')
  const [error, setError] = useState<string | null>(null)
  const [proposal, setProposal] = useState<{ unmatched: string[]; dropped: number } | null>(null)
  const [merged, setMerged] = useState<ProposalCell[]>([])
  /*
   * What each proposed cell will write, keyed player:hole, as typed. Editable
   * because the model reads a scrawled 4 as a 9 exactly often enough to need
   * fixing on the spot, and blank means unticked — an empty box writes nothing,
   * which is the same statement it makes on the grid itself.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const group = groups.find((g) => g.id === groupId)

  const onFile = async (file: File | undefined) => {
    if (!file || !group) return
    setError(null)
    setPhase('reading')
    try {
      const image = await toJpegBase64(file)
      const read = await readCardPhoto(roundId, groupId, image, 'image/jpeg')
      /*
       * The judgement is frozen at the moment the proposal arrives, not
       * re-derived on every render — a score landing from another phone
       * mid-review must not silently re-file a ticked cell as a conflict while
       * the scorer's thumb is over Confirm. The confirm step re-checks
       * emptiness against the live fold before writing, which is the half of
       * that race that actually matters.
       */
      const judged = mergeProposal(roundId, read.cells, mergedScores(getState()))
      setMerged(judged)
      setProposal({ unmatched: read.unmatched, dropped: read.dropped })
      setDrafts(
        Object.fromEntries(
          judged
            .filter((c): c is Extract<ProposalCell, { kind: 'propose' }> => c.kind === 'propose')
            .map((c) => [`${c.playerId}:${c.holeNo}`, String(c.gross)]),
        ),
      )
      setPhase('review')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that photo.')
      setPhase('pick')
    }
  }

  /** A draft that will actually be written: a whole number a scorecard can hold. */
  const draftGross = (key: string): number | null => {
    const raw = (drafts[key] ?? '').trim()
    if (raw === '') return null
    const n = Number(raw)
    // Same line the grid draws for typed scores — this IS a typed score by the
    // time the scorer has edited it.
    return Number.isInteger(n) && n >= 1 && n <= 20 ? n : null
  }

  const proposeCells = merged.filter((c) => c.kind === 'propose')
  const willWrite = proposeCells.filter((c) => draftGross(`${c.playerId}:${c.holeNo}`) !== null)
  const conflicts = merged.filter((c) => c.kind === 'conflict').length
  const agreements = merged.filter((c) => c.kind === 'agree').length

  const confirm = () => {
    /*
     * Re-read the fold NOW, not the one the review was built from. Somebody in
     * the group may have entered the 7th by hand while this screen was open,
     * and "only into empty cells" is a promise about the moment of writing,
     * not about the moment of photographing. A cell that filled in the
     * meantime is skipped: the number that got there first was typed by a
     * person, and the grid this lands back on will show it.
     */
    const current = mergedScores(getState())
    for (const cell of proposeCells) {
      const gross = draftGross(`${cell.playerId}:${cell.holeNo}`)
      if (gross === null) continue
      const filled = current.some(
        (s) => s.roundId === roundId && s.playerId === cell.playerId && s.holeNo === cell.holeNo,
      )
      if (filled) continue
      // The only write this feature ever makes, and it is the ordinary one.
      setScore(roundId, cell.playerId, cell.holeNo, { state: 'holed', gross })
    }
    // Land back on the grid, where the numbers just went.
    onClose()
  }

  const cellFor = (pid: string, holeNo: number): ProposalCell | undefined =>
    merged.find((c) => c.playerId === pid && c.holeNo === holeNo)

  const currentLabel = (pid: string, holeNo: number): string => {
    const v = scores.find(
      (s) => s.roundId === roundId && s.playerId === pid && s.holeNo === holeNo,
    )?.value
    return v ? existingLabel(v) : ''
  }

  if (!round || !course || !group) {
    // A round or group that vanished mid-flow (an admin re-drew the groups) has
    // nothing to backfill into; closing is the only honest control left.
    return (
      <div className="fixed inset-0 z-50 bg-paper flex flex-col">
        <div className="flex items-center gap-3 px-4 py-3 pt-safe bg-ink text-paper">
          <span className="bc-label flex-1 truncate">From a photo of the card</span>
          <button
            type="button"
            onClick={onClose}
            className="bc-tap bc-label border-2 border-paper px-3 py-1.5"
          >
            Close
          </button>
        </div>
        <p className="px-4 py-6 text-sm text-mute">This round has no group to read a card for.</p>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-paper text-ink flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3 pt-safe bg-ink text-paper">
        <span className="bc-label flex-1 truncate">
          {course.name} · {round.label} — from a photo
        </span>
        <button
          type="button"
          onClick={onClose}
          className="bc-tap bc-label border-2 border-paper px-3 py-1.5"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {phase === 'pick' && (
          <div className="p-4 grid gap-4">
            <p className="text-[13px] text-mute leading-snug">
              Photograph the paper card and the app will propose the scores it can read.
              Nothing is entered until you confirm it here, and nothing already on the
              grid is ever changed — the photo only fills in blanks.
            </p>

            {groups.length > 1 && (
              <label className="grid gap-1">
                <span className="bc-label text-mute">Whose card</span>
                <select
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                  className="bc-tap border border-ink px-2 py-2 bc-label bg-paper w-full"
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {clockTime(g.teeTime)} · {g.playerIds.map((p) => playerName(doc, p)).join(', ')}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/*
              One button, no `capture` attribute — and the absence is the
              feature. A bare file input with accept="image/*" makes iOS pop
              its own sheet: Photo Library, Take Photo, Choose File. Adding
              `capture` collapses that menu to the camera alone, which is how
              an earlier version locked the library out, and the two-button
              replacement was answering a question the OS already asks better.
              The input is sr-only because a file input cannot be styled into a
              button; the label is the 44px control.
            */}
            <div className="grid gap-1">
              <span className="bc-label text-mute">The photo</span>
              <label className="bc-tap block border-2 border-ink bg-ink text-paper bc-label text-center px-3 py-3 w-full">
                Camera or photo library
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    // Cleared so the retake after a failed read still fires — a
                    // file input does not change when it is handed the same
                    // value twice, and "try again" with the same photo is the
                    // first thing everybody does.
                    e.target.value = ''
                    void onFile(file)
                  }}
                  className="sr-only"
                />
              </label>
            </div>

            {error && <p className="bg-signal text-paper px-3 py-2 text-xs">{error}</p>}
          </div>
        )}

        {phase === 'reading' && (
          <p className="px-4 py-10 text-center text-mute text-sm">Reading the card…</p>
        )}

        {phase === 'review' && (
          <>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse min-w-max">
                <thead>
                  <tr className="bg-ink text-paper">
                    <th className="bc-label text-left px-3 py-2 sticky left-0 bg-ink z-10">Hole</th>
                    {holes.map((h) => (
                      <th key={h.number} className="bc-num px-1 py-2 w-11">
                        {h.number}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.playerIds.map((pid) => (
                    <tr key={pid} className="border-b border-rule">
                      <td className="bc-name px-3 py-1 sticky left-0 bg-paper z-10 whitespace-nowrap">
                        {playerName(doc, pid)}
                      </td>
                      {holes.map((h) => {
                        const cell = cellFor(pid, h.number)
                        if (cell?.kind === 'propose') {
                          return (
                            <td key={h.number} className="p-0.5">
                              <input
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={2}
                                value={drafts[`${pid}:${h.number}`] ?? ''}
                                onChange={(e) =>
                                  setDrafts({
                                    ...drafts,
                                    [`${pid}:${h.number}`]: e.target.value,
                                  })
                                }
                                aria-label={`${playerName(doc, pid)} hole ${h.number}, proposed from the photo`}
                                /*
                                 * `bc-tap w-10 h-10` for the same reason the
                                 * grid's cells carry it: this is the same dense
                                 * table, reviewed with the same cold thumbs,
                                 * and a near-miss edits the neighboring hole.
                                 * Signal border because these are the cells
                                 * that will WRITE — the one thing on this
                                 * screen that needs to look armed.
                                 */
                                className="bc-tap w-10 h-10 text-center bc-num border-2 border-signal bg-paper"
                              />
                            </td>
                          )
                        }
                        if (cell?.kind === 'agree') {
                          return (
                            <td key={h.number} className="p-0.5">
                              <div className="w-10 h-10 flex items-center justify-center bc-num text-mute">
                                {cell.gross}
                              </div>
                            </td>
                          )
                        }
                        if (cell?.kind === 'conflict') {
                          return (
                            <td key={h.number} className="p-0.5">
                              {/* The existing number, standing; the photo's
                                  reading struck through under it. Loud on
                                  purpose — this is the cell worth walking over
                                  to the group about — but not a control: the
                                  card on the grid wins, and changing it is the
                                  normal score-edit path. */}
                              <div className="w-10 h-10 flex flex-col items-center justify-center bg-signal text-paper">
                                <span className="bc-num leading-none">{cell.existing}</span>
                                <span className="text-[9px] line-through leading-none">
                                  {cell.proposed}
                                </span>
                              </div>
                            </td>
                          )
                        }
                        return (
                          <td key={h.number} className="p-0.5">
                            <div className="w-10 h-10 flex items-center justify-center bc-num text-dim">
                              {currentLabel(pid, h.number) || '·'}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 grid gap-3">
              <p className="text-[13px] text-mute leading-snug">
                {willWrite.length > 0
                  ? `${willWrite.length} score${willWrite.length === 1 ? '' : 's'} will be entered into empty boxes. `
                  : 'Nothing new to enter from this photo. '}
                {agreements > 0 && `${agreements} already on the grid and matching. `}
                {conflicts > 0 &&
                  `${conflicts} disagree${conflicts === 1 ? 's' : ''} with the grid — the grid wins here; if the paper is right, fix it through normal score entry. `}
                Clear a red box to leave that hole alone.
              </p>

              {proposal && proposal.unmatched.length > 0 && (
                <p className="text-[13px] leading-snug">
                  Names on the card that match nobody in this group:{' '}
                  <b className="bc-name">{proposal.unmatched.join(', ')}</b>. Those rows were
                  left alone — enter them by hand.
                </p>
              )}
              {proposal && proposal.dropped > 0 && (
                <p className="text-[13px] text-mute leading-snug">
                  {proposal.dropped} cell{proposal.dropped === 1 ? ' was' : 's were'} unreadable
                  or impossible and ignored.
                </p>
              )}

              <button
                type="button"
                onClick={confirm}
                disabled={willWrite.length === 0}
                className="bc-tap bg-signal text-paper bc-label py-4 disabled:opacity-40"
              >
                {willWrite.length === 0
                  ? 'Nothing to enter'
                  : `Enter ${willWrite.length} score${willWrite.length === 1 ? '' : 's'}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPhase('pick')
                  setMerged([])
                  setProposal(null)
                  setDrafts({})
                }}
                className="bc-tap bc-label border border-ink py-3"
              >
                Try another photo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
